import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, } from 'react';
import { View } from 'react-native';
import { useScrollToTop } from '@react-navigation/native';
import LinearGradient from 'utils/packages/linearGradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { rem } from 'design-system/values';
import { Spacer } from 'design-system/Spacer';
import { AppView } from 'design-system/AppView';
import Button, { HEIGHT } from 'design-system/Button';
import { CardView } from 'design-system/CardView';
import StatusBar from 'design-system/StatusBar';
import ScrollView from 'design-system/ScrollView';
import Text from 'design-system/Text';
import { Tier, tierGreaterThan, TierPeriod, } from 'features/premium/entities';
import { selectAvailableIAPSubscriptionsWithPrices, selectBlinkistBackFloMonthly, selectBlinkistBackFloYearlyTiers, selectBlinkistBackTryFor0, selectBlinkistBackV4, selectBlinkistBackV4Monthly, selectClearPricingMode, selectDefaultPaywallTier, selectEligibleFreeTrialDays, selectShouldShowStripe, selectSkipFreeTrialAB, selectStripePriceMap, selectSubscriptionPrices, } from 'features/premium/selectors';
import { selectDefaultCurrency, selectFeatureFlag, selectIsLoggedIn, } from 'reducers/selectors';
import { useAppSelector, useAppStore } from 'store/hooks';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { TAB_SCREEN_HEADER_HEIGHT } from 'navigators/styles';
import { useHeaderHeight } from 'hooks';
import { getTierSubscriptions, getYearlyTierSubscriptions, } from 'features/premium/hooks';
import createStyleSheets from 'utils/createStyleSheets';
import Links from 'utils/links';
import useStyles from 'hooks/useStyles';
import { BenefitHeader } from 'features/premium/components/PremiumFeatureMicroModalContent';
import { formatTryFor0 } from 'utils';
import useColors from 'hooks/useColors';
import { usePromoDiscountBanner } from 'features/promotions/hooks';
import Modal from 'utils/packages/Modal';
import { useActivatePremiumTrialModal } from '../hooks/useActivatePremiumTrialModal';
import { useDelayedModal } from '../hooks/useDelayedModal';
import HowFreeTrialWorksAllPlansButton from './HowFreeTrialWorksAllPlansButton';
import HowFreeTrialWorksPricesView from './HowFreeTrialWorksPricesView';
import NewFreeTrialList from './NewFreeTrialList';
import { transformSubscriptionsToDisplayData, transformTierSubscriptionsToDisplayData, } from '../utils/subscriptionPricing';
import { PlanSelectionList } from './PlanSelectionList';
import ScrollingReviews from './ScrollingReviews';
import AnimatedPaywallButton from './AnimatedPaywallButton';
const HowFreeTrialWorksViewComponent = forwardRef(({ tier, adjustForBottomTabBar = false, promoProductId, subscriptions, benefitId, onUpgradeComplete, onViewAllPlansPressed, upsellTrial = true, analyticsFlow, displayOnlyMonthly, isFloPaywallC1, hideViewAllPlansOnboarding, isMicroModal = false, isOnboardingFlow, }, ref) => {
    const colors = useColors();
    const blinkistBackTryFor0 = useAppSelector(selectBlinkistBackTryFor0);
    const blinkistBackV4Value = useAppSelector(selectBlinkistBackV4);
    const blinkistBackV4Monthly = useAppSelector(selectBlinkistBackV4Monthly);
    const blinkistBackFloMonthly = useAppSelector(selectBlinkistBackFloMonthly);
    const clearPricingMode = useAppSelector(selectClearPricingMode) ?? undefined;
    const blinkistBackV4 = blinkistBackV4Value && !isMicroModal;
    // The `flo_preselected_monthly` and `v4_preselected_monthly` variants pre-select the monthly plan
    // instead of the default yearly (index 0).
    const preSelectMonthly = blinkistBackFloMonthly || blinkistBackV4Monthly;
    const promotedTier = useMemo(() => promoProductId
        ? subscriptions.find((s) => s.productId === promoProductId)?.tier
        : undefined, [promoProductId, subscriptions]);
    const defaultPaywallTier = useAppSelector(selectDefaultPaywallTier);
    const selectedTier = promotedTier ??
        (tierGreaterThan(tier, Tier.plus) || upsellTrial === false
            ? tier
            : defaultPaywallTier) ??
        defaultPaywallTier;
    const { resetTimer, pauseTimer, setBlocked } = useDelayedModal('didInteractOnPremiumTrialHowItWorksScreen', defaultPaywallTier, TierPeriod.yearly, analyticsFlow, onUpgradeComplete, benefitId);
    const handleUserInteraction = useCallback(() => {
        resetTimer();
    }, [resetTimer]);
    const pauseUserInteraction = useCallback(() => {
        pauseTimer();
    }, [pauseTimer]);
    // A/B test
    const [enableFreeTrial, setEnableFreeTrial] = useState(false);
    const skipFreeTrialAB = useAppSelector(selectSkipFreeTrialAB);
    const skipFreeTrial = skipFreeTrialAB ? !enableFreeTrial : false;
    const { prices, isFetching, openUpgradeModal, openUpgradeModalWithSubscriptions, } = useActivatePremiumTrialModal({
        selectedTier,
        onUpgradeComplete,
        analyticsFlow,
        benefitId,
    });
    useEffect(() => {
        setBlocked(isFetching);
    }, [isFetching, setBlocked]);
    // This is a bit on an anti-pattern but it’s either this or break the encapsulation of this component
    useImperativeHandle(ref, () => ({
        openUpgradeModal,
        handleUserInteraction,
        pauseUserInteraction,
        selectedTier,
    }), [
        handleUserInteraction,
        openUpgradeModal,
        pauseUserInteraction,
        selectedTier,
    ]);
    const { paddingHorizontalStyle, height } = useAppFrameDimensions(adjustForBottomTabBar);
    const contentContainerStyles = useMarginBottom('paddingBottom', HEIGHT + rem(16));
    const subscriptionYearlyPrice = prices?.[TierPeriod.yearly][selectedTier];
    const subscriptionMonthlyPrice = prices?.[TierPeriod.monthly][selectedTier];
    const subscriptionToShow = displayOnlyMonthly
        ? subscriptionMonthlyPrice
        : subscriptionYearlyPrice;
    const store = useAppStore();
    const show7DayTrialButtonText = useAppSelector((store) => selectFeatureFlag(store, 'premium_start_7_day_trial_button_text').value);
    const eligibleFreeTrialDays = useAppSelector(selectEligibleFreeTrialDays);
    const { bottom } = useSafeAreaInsets();
    const handleViewAllPlansPress = useCallback(() => {
        onViewAllPlansPressed(selectedTier);
    }, [onViewAllPlansPressed, selectedTier]);
    const scrollViewRef = useRef(null);
    useScrollToTop(scrollViewRef);
    const hh = useHeaderHeight();
    const availableSubscriptions = useAppSelector(selectAvailableIAPSubscriptionsWithPrices);
    const stripePriceMap = useAppSelector(selectStripePriceMap);
    const canUseStripe = useAppSelector(selectShouldShowStripe);
    const floYearlyTiersValue = useAppSelector(selectBlinkistBackFloYearlyTiers);
    const floYearlyTiers = isFloPaywallC1 && !isMicroModal ? floYearlyTiersValue : null;
    const currentSubscriptions = useMemo(() => (floYearlyTiers
        ? getYearlyTierSubscriptions(availableSubscriptions, floYearlyTiers, canUseStripe, stripePriceMap)
        : getTierSubscriptions(availableSubscriptions, selectedTier, canUseStripe, stripePriceMap)).filter(Boolean), [
        availableSubscriptions,
        canUseStripe,
        floYearlyTiers,
        selectedTier,
        stripePriceMap,
    ]);
    const monthlyIndex = currentSubscriptions.findIndex((sub) => sub?.period === TierPeriod.monthly);
    const [selection, setSelection] = useState(preSelectMonthly && monthlyIndex >= 0 ? monthlyIndex : 0);
    const data = useMemo(() => {
        if ((!isFloPaywallC1 && !blinkistBackV4) || !currentSubscriptions?.length)
            return [];
        if (floYearlyTiers) {
            return transformTierSubscriptionsToDisplayData(currentSubscriptions, {
                tier: floYearlyTiers[0],
                label: 'MOST POPULAR',
            });
        }
        return transformSubscriptionsToDisplayData(currentSubscriptions);
    }, [blinkistBackV4, currentSubscriptions, floYearlyTiers, isFloPaywallC1]);
    const isLoggedIn = useAppSelector(selectIsLoggedIn);
    const discountBanner = usePromoDiscountBanner();
    const onPressContinue = useCallback(() => {
        const cb = () => {
            pauseUserInteraction();
            // It’s important to get these from the store in case the retry function has updated them
            const prices = selectSubscriptionPrices(store.getState());
            const subscriptionMonthlyPrice = prices?.[TierPeriod.monthly][selectedTier];
            if (isFloPaywallC1 || blinkistBackV4) {
                openUpgradeModalWithSubscriptions([currentSubscriptions[selection]], onPressContinue, skipFreeTrial);
                return;
            }
            if (displayOnlyMonthly) {
                openUpgradeModalWithSubscriptions([subscriptionMonthlyPrice], onPressContinue, false);
                return;
            }
            openUpgradeModal(false);
        };
        if (!discountBanner || !isLoggedIn) {
            cb();
            return;
        }
        Modal.showConfirmation({
            title: 'You have an active offer',
            subtitle: `You have a limited time offer for ${discountBanner.productName} ${discountBanner.matchedSubscription.period === TierPeriod.monthly
                ? 'monthly'
                : 'yearly'} plan. Do you want to explore it?`,
            confirmButtonTitle: 'Skip the offer',
            cancelButtonTitle: 'Discover the offer',
            onConfirm: cb,
            onPressCancel: discountBanner.onOpen,
        });
    }, [
        blinkistBackV4,
        currentSubscriptions,
        discountBanner,
        displayOnlyMonthly,
        isFloPaywallC1,
        isLoggedIn,
        openUpgradeModal,
        openUpgradeModalWithSubscriptions,
        pauseUserInteraction,
        selectedTier,
        selection,
        skipFreeTrial,
    ]);
    const styles = useStyles(styleSet);
    const handleTnc = useCallback(() => {
        Links.openKnownLink('paidPlanTncs');
    }, []);
    const userCurrency = useAppSelector(selectDefaultCurrency);
    const bottomPadding = adjustForBottomTabBar ? rem(16) : bottom || rem(16);
    if (isMicroModal || !isFloPaywallC1) {
        return (<AppView withFrame={false}>
          <StatusBar barStyle="dark-content" animated/>
          <ScrollView secondary bounces={!blinkistBackV4} ref={scrollViewRef} finalHeaderHeight={adjustForBottomTabBar ? TAB_SCREEN_HEADER_HEIGHT : undefined} expandedHeaderHeight={blinkistBackV4 ? hh : undefined} contentContainerStyle={
            // eslint-disable-next-line no-nested-ternary
            blinkistBackV4
                ? paddingHorizontalStyle
                : adjustForBottomTabBar
                    ? [styles.contentContainerStyle, paddingHorizontalStyle]
                    : contentContainerStyles}>
            {isMicroModal && benefitId ? (<BenefitHeader benefitId={benefitId} withMargin/>) : (<>
                <Text centered Text-24>
                  {height / rem(1) < 850
                    ? 'How your free trial works'
                    : 'How your free\ntrial works'}
                </Text>
                {!blinkistBackV4 && <Spacer h={8}/>}
              </>)}
            {blinkistBackV4 ? (<LinearGradient style={styles.linearGradient} locations={[0.8, 1]} colors={[
                    `${String(colors.cards.onDark)}`,
                    `${String(colors.cards.onDark)}00`,
                ]}>
                <NewFreeTrialList longLastAbsLine selectedTier={selectedTier} eligibleFreeTrialDays={eligibleFreeTrialDays} style={!!blinkistBackV4 && styles.newFreeTrialListMicroModal}/>
              </LinearGradient>) : (<CardView>
                <NewFreeTrialList selectedTier={selectedTier} eligibleFreeTrialDays={eligibleFreeTrialDays} style={!!isMicroModal && styles.newFreeTrialListMicroModal} isMicroModal={isMicroModal}/>
              </CardView>)}
            {!blinkistBackV4 && (<CardView noMarginTop noMarginBottom style={[
                    styles.trialDescription,
                    isMicroModal && styles.trialDescriptionMicroModal,
                ]}>
                {!!subscriptionToShow && (<HowFreeTrialWorksPricesView subscription={subscriptionToShow}/>)}
                <HowFreeTrialWorksAllPlansButton onPress={handleViewAllPlansPress} style={!!isMicroModal && styles.allPlansButtonMicroModal}/>
              </CardView>)}
          </ScrollView>
          {!!blinkistBackV4 && (<View style={paddingHorizontalStyle}>
              <PlanSelectionList data={data} hideRadioBtn={!clearPricingMode} hideFreeTrialSwitch selection={selection} withPersistentBounce priceDisplayMode={clearPricingMode} onSelectionChange={setSelection} onPressIn={pauseUserInteraction} enableFreeTrial={enableFreeTrial} onPressOut={handleUserInteraction} isOnboardingFlow={isOnboardingFlow} containerStyle={styles.planSelectionList} onEnableFreeTrialChange={setEnableFreeTrial}/>
            </View>)}
          <View style={[
                styles.continueButton,
                paddingHorizontalStyle,
                blinkistBackV4 && styles.relativeBtn,
                blinkistBackV4
                    ? { marginBottom: bottomPadding }
                    : { bottom: bottomPadding },
            ]}>
            {!!blinkistBackV4 && (<>
                <Text centered TextThin-14 Secondary style={styles.tncText}>
                  Try {eligibleFreeTrialDays} days free. Cancel anytime.{' '}
                  <Text Primary TextThin-14 onPress={handleTnc} suppressHighlighting>
                    T&Cs
                  </Text>{' '}
                  apply
                </Text>
                <Spacer h={8}/>
              </>)}
            <Button brand isFetching={isFetching} onPress={onPressContinue} title={
            // eslint-disable-next-line no-nested-ternary
            blinkistBackTryFor0 || blinkistBackV4
                ? formatTryFor0(userCurrency)
                : show7DayTrialButtonText
                    ? `Start ${eligibleFreeTrialDays}-day trial`
                    : 'Continue'} onPressIn={pauseUserInteraction} onPressOut={handleUserInteraction}/>
            {!!blinkistBackV4 && (<>
                <Spacer h={8}/>
                <HowFreeTrialWorksAllPlansButton onPress={handleViewAllPlansPress} onPressIn={pauseUserInteraction} onPressOut={handleUserInteraction} style={!!blinkistBackV4 && styles.allPlansButtonMicroModal}/>
              </>)}
          </View>
        </AppView>);
    }
    return (<AppView withFrame={false}>
        <StatusBar barStyle="dark-content" animated/>
        <ScrollView secondary ref={scrollViewRef} expandedHeaderHeight={hh} onTouchStart={pauseUserInteraction} onTouchEnd={handleUserInteraction}>
          <Text centered Text-24>
            Choose your plan
          </Text>

          <ScrollingReviews withShimmeringStars/>
        </ScrollView>
        <View style={[
            paddingHorizontalStyle,
            { paddingBottom: bottom || rem(16), marginHorizontal: rem(16) },
        ]}>
          <PlanSelectionList data={data} selection={selection} onSelectionChange={setSelection} isOnboardingFlow={isOnboardingFlow} onPressIn={pauseUserInteraction} onPressOut={handleUserInteraction} enableFreeTrial={enableFreeTrial} onEnableFreeTrialChange={setEnableFreeTrial} priceDisplayMode={clearPricingMode} withoutGradient withPersistentBounce withShiningEffect changeSwitchText/>
          <Spacer.H16 />
          {!hideViewAllPlansOnboarding && (<>
              <Text TextThin-14 Secondary centered>
                Cancel your plan at any time.{' '}
                <Text TextThin-14 Primary suppressHighlighting onPress={handleTnc}>
                  T&Cs
                </Text>{' '}
                apply
              </Text>
              <Spacer h={12}/>
            </>)}
          <AnimatedPaywallButton withCtaSlam withCtaGlow isFetching={isFetching} onPressContinue={onPressContinue} enableFreeTrial={enableFreeTrial} pauseUserInteraction={pauseUserInteraction} handleUserInteraction={handleUserInteraction}/>
          <Spacer.H4 />
          {hideViewAllPlansOnboarding ? (<Text centered TextThin-14 Secondary style={styles.tncText}>
              Cancel your plan at any time.{' '}
              <Text Primary TextThin-14 onPress={handleTnc} suppressHighlighting>
                T&Cs
              </Text>{' '}
              apply
            </Text>) : (<HowFreeTrialWorksAllPlansButton onPress={handleViewAllPlansPress} onPressIn={pauseUserInteraction} onPressOut={handleUserInteraction}/>)}
        </View>
      </AppView>);
});
export const HowFreeTrialWorksView = React.memo(HowFreeTrialWorksViewComponent);
const styleSet = createStyleSheets(() => ({
    contentContainerStyle: {
        paddingBottom: HEIGHT + rem(32),
    },
    trialDescription: {
        alignItems: 'center',
        paddingHorizontal: rem(16),
        paddingTop: rem(24),
        paddingBottom: rem(16),
    },
    trialDescriptionMicroModal: {
        paddingTop: rem(16),
        paddingBottom: rem(8),
    },
    newFreeTrialListMicroModal: {
        paddingHorizontal: rem(24),
        paddingVertical: rem(20),
    },
    allPlansButtonMicroModal: {
        padding: rem(8),
    },
    continueButton: {
        position: 'absolute',
        left: rem(16),
        right: rem(16),
    },
    relativeBtn: {
        position: 'relative',
        left: 0,
        right: 0,
        marginHorizontal: rem(16),
        bottom: 0,
    },
    tncText: {
        marginTop: rem(12),
    },
    linearGradient: {
        margin: rem(16),
        marginBottom: rem(38),
        borderRadius: rem(16),
    },
    planSelectionList: {
        paddingHorizontal: rem(16),
        marginTop: rem(-12),
    },
}));
